import { FC, MouseEvent, useCallback, useEffect } from 'react';
import { useFragment } from 'react-relay';
import { useField } from 'formik';
import { isEmpty } from 'lodash';
import { OutputData } from '@editorjs/editorjs';
import graphql from 'babel-plugin-relay/macro';

import ErrorIcon from '@mui/icons-material/Error';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import ConfirmableIconButton from 'components/ConfirmableIconButton';

import { useEditor } from 'components/EditorJsField';
import { useAvoidSuspense } from 'lib';

import Query, {
  EditorTranslateButtonRefetchQuery,
  Locale,
} from '__generated__/EditorTranslateButtonRefetchQuery.graphql';
import { EditorTranslateButton_to$key } from '__generated__/EditorTranslateButton_to.graphql';
import { EditorTranslateButton_textBlock$key } from '__generated__/EditorTranslateButton_textBlock.graphql';
import { EditorTranslateButton_from$key } from '__generated__/EditorTranslateButton_from.graphql';
import { EditorTranslateButton_data$key } from '__generated__/EditorTranslateButton_data.graphql';

type Props = {
  from: EditorTranslateButton_from$key;
  to: EditorTranslateButton_to$key;
  textBlock: EditorTranslateButton_textBlock$key | null;
  data: EditorTranslateButton_data$key;
  onTranslate: (data: OutputData, locale: Locale) => void;
};

const EditorTranslateButton: FC<Props> = props => {
  const { onTranslate } = props;
  const { name, editorCore } = useEditor();

  const from = useFragment(
    graphql`
      fragment EditorTranslateButton_from on Language {
        locale
      }
    `,
    props.from
  );

  const to = useFragment(
    graphql`
      fragment EditorTranslateButton_to on Language {
        locale
        readOnly
      }
    `,
    props.to
  );

  const [{ value }] = useField<OutputData | null>(`${name}.${to.locale}`);

  const textBlock = useFragment(
    graphql`
      fragment EditorTranslateButton_textBlock on TextBlock {
        id
        data
      }
    `,
    props.textBlock
  );

  const [{ translateTextBlock }, translation] = useAvoidSuspense<
    EditorTranslateButtonRefetchQuery,
    EditorTranslateButton_data$key
  >(
    graphql`
      fragment EditorTranslateButton_data on Query
      @refetchable(queryName: "EditorTranslateButtonRefetchQuery")
      @argumentDefinitions(
        translate: { type: "Boolean", defaultValue: false }
      ) {
        translateTextBlock(id: $id, to: $to) @include(if: $translate)
      }
    `,
    props.data,
    Query
  );

  const disabled =
    !textBlock?.data ||
    isEmpty((JSON.parse(textBlock.data) as OutputData).blocks) ||
    to.readOnly;

  const handleTranslateClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();

      if (!textBlock) return;

      const vars = {
        id: textBlock.id,
        to: to.locale,
        translate: true,
      };

      translation.refetch(vars);
    },
    [textBlock, to.locale, translation]
  );

  useEffect(() => {
    const editor = editorCore(to.locale);

    if (editor && translateTextBlock) {
      const blocks = JSON.parse(translateTextBlock);
      console.log(blocks);

      editor.render({ blocks }).then(async () => {
        onTranslate(await editor.save(), to.locale);
      });
    }
  }, [editorCore, onTranslate, to.locale, translateTextBlock]);

  return (
    <Tooltip title={`Versti iš ${from.locale}`}>
      <span>
        <ConfirmableIconButton
          color="default"
          size="small"
          disabled={disabled}
          enabled={
            Boolean(value && value.blocks.length > 0) && !translation.loading
          }
          confirmIcon={<ErrorIcon fontSize="small" color="error" />}
          onConfirm={handleTranslateClick}>
          <Badge color="primary" badgeContent={from.locale.toLocaleLowerCase()}>
            {translation.loading ? (
              <CircularProgress size="1em" />
            ) : (
              <GTranslateIcon fontSize="small" />
            )}
          </Badge>
        </ConfirmableIconButton>
      </span>
    </Tooltip>
  );
};

export default EditorTranslateButton;
